import React, { useCallback, useEffect, useState } from 'react';
import api from '../../api';

// Reiter „Team" im Training: Trainer und Helfer einchecken (Rolle kommt vom Server)

// 'YYYY-MM-DD' → „Montag, 05.10.2026" (ohne Zeitzonen-Verschiebung)
function fmtDatum(iso) {
  if (!iso) return '';
  return new Date(`${String(iso).slice(0, 10)}T12:00:00Z`)
    .toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
}
const terminText = (t) => `${t.wochentag_name} ${t.von}${t.bis ? `–${t.bis}` : ''} Uhr`;

export default function TrainerTeam({ einheitId }) {
  const [daten, setDaten] = useState(null);
  const [fehler, setFehler] = useState('');
  const [arbeitet, setArbeitet] = useState({});
  const [suche, setSuche] = useState('');

  const load = useCallback(() => {
    api.get(`/api/trainer/einheiten/${einheitId}/team`)
      .then(r => setDaten(r.data))
      .catch(() => setFehler('Team konnte nicht geladen werden'));
  }, [einheitId]);
  useEffect(() => { load(); }, [load]);

  if (!daten) return fehler ? <div className="alert alert-error">{fehler}</div> : <div style={{ textAlign: 'center', padding: '2rem' }}><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>;

  const drin = new Set(daten.eingecheckt.map(e => e.mitglied_id));
  const umschalten = async (p) => {
    setArbeitet(a => ({ ...a, [p.id]: true })); setFehler('');
    try {
      if (drin.has(p.id)) await api.delete(`/api/trainer/einheiten/${einheitId}/team/${p.id}`);
      else await api.post(`/api/trainer/einheiten/${einheitId}/team`, { mitglied_id: p.id });
      load();
    } catch (err) {
      setFehler(err.response?.data?.error || 'Konnte nicht gespeichert werden');
    } finally { setArbeitet(a => ({ ...a, [p.id]: false })); }
  };

  const kannAendern = daten.darf_bearbeiten && !!daten.zeit;
  const stamm = daten.personen.filter(p => p.stammteam || drin.has(p.id));
  const weitere = daten.personen.filter(p => !p.stammteam && !drin.has(p.id));
  const treffer = suche.trim().length >= 2
    ? weitere.filter(p => `${p.vorname} ${p.nachname}`.toLowerCase().includes(suche.trim().toLowerCase()))
    : [];
  const stunden = daten.eingecheckt.reduce((s, e) => s + e.stunden, 0);

  const zeile = (p) => {
    const an = drin.has(p.id);
    return (
      <button key={p.id} type="button" onClick={() => kannAendern && !arbeitet[p.id] && umschalten(p)} disabled={!kannAendern}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%', textAlign: 'left',
          padding: '0.8rem 1rem', border: 'none', borderBottom: '1px solid #f3f3f3', cursor: kannAendern ? 'pointer' : 'default',
          background: an ? '#f1f8f1' : '#fff', color: '#222',
        }}>
        <span style={{
          width: 40, height: 40, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: an ? '#4caf50' : '#e0e0e0', color: '#fff', fontWeight: 700, fontSize: '1.1rem',
        }}>{arbeitet[p.id] ? '…' : an ? '✓' : ''}</span>
        <span style={{ flex: 1 }}>
          <span style={{ display: 'block', fontWeight: 600 }}>{p.vorname} {p.nachname}</span>
          <span style={{ fontSize: '0.75rem', color: '#888' }}>{p.rolle}{p.stammteam ? '' : ' · Vertretung'}</span>
        </span>
      </button>
    );
  };

  return (
    <div>
      <div style={{ background: '#fff', borderRadius: 12, padding: '0.8rem 1rem', marginBottom: '0.75rem', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', fontSize: '0.85rem' }}>
        {/* Immer zuerst: um welche Gruppe und welchen Tag es hier geht */}
        <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: 4 }}>{daten.gruppe}</div>
        <div style={{ color: '#666', marginBottom: 6 }}>{fmtDatum(daten.datum)}</div>
        {daten.zeit
          ? <>Trainingszeit <strong>{daten.zeit.von}–{daten.zeit.bis}</strong> = <strong>{String(daten.zeit.stunden).replace('.', ',')} Std.</strong> pro Person</>
          : (
            <div style={{ color: '#c62828' }}>
              <div><strong>{daten.gruppe}</strong> trainiert {daten.wochentag_name ? `${daten.wochentag_name.toLowerCase()}s` : 'an diesem Wochentag'} nicht – ohne Trainingszeit lassen sich keine Stunden erfassen.
              </div>
              <div style={{ color: '#555', marginTop: 4 }}>
                {daten.termine?.length
                  ? <>Hinterlegt {daten.termine.length === 1 ? 'ist' : 'sind'}: {daten.termine.map(terminText).join(' · ')}</>
                  : <>Für diese Gruppe ist noch gar kein Trainingstermin hinterlegt.</>}
              </div>
              <div style={{ color: '#555', marginTop: 4 }}>
                Anwesenheit der Kinder kannst du trotzdem erfassen. Für die Stunden bitte dem Schwimmadmin melden.
              </div>
            </div>
          )}
        <div style={{ color: '#666', marginTop: 6 }}>Eingecheckt: {daten.eingecheckt.length} Personen · {String(Math.round(stunden * 100) / 100).replace('.', ',')} Std.</div>
        {daten.gesperrt && <div style={{ color: '#c62828', marginTop: 4 }}>🔒 Das Quartal ist abgeschlossen – keine Änderungen mehr möglich.</div>}
        {!daten.gesperrt && !daten.darf_bearbeiten && <div style={{ color: '#e65100', marginTop: 4 }}>Nur Trainer dieser Gruppe können einchecken.</div>}
      </div>

      {fehler && <div className="alert alert-error">{fehler}</div>}

      <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: '0.75rem' }}>
        <div style={{ padding: '0.6rem 1rem', fontWeight: 700, fontSize: '0.85rem', borderBottom: '1px solid #f0f0f0' }}>Team dieser Gruppe</div>
        {stamm.length === 0 && <div style={{ padding: '1rem', color: '#999', fontSize: '0.85rem' }}>Kein Stammteam hinterlegt – Personen unten suchen.</div>}
        {stamm.map(zeile)}
      </div>

      {kannAendern && (
        <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <div style={{ padding: '0.6rem 1rem', fontWeight: 700, fontSize: '0.85rem' }}>Vertretung / weitere Person</div>
          <div style={{ padding: '0 1rem 0.75rem' }}>
            <input type="search" value={suche} onChange={e => setSuche(e.target.value)} placeholder="Name suchen…"
              style={{ width: '100%', padding: '0.6rem 0.75rem', fontSize: '16px', border: '1px solid #ddd', borderRadius: 8, boxSizing: 'border-box' }} />
          </div>
          {treffer.map(zeile)}
          {suche.trim().length >= 2 && treffer.length === 0 && <div style={{ padding: '0 1rem 1rem', color: '#999', fontSize: '0.85rem' }}>Keine Person gefunden</div>}
        </div>
      )}
    </div>
  );
}
